<template lang="pug">
  .reactive-to-items-carousel
    .carousel-wrapper(ref="elementsWrapper")
      .the-carousel(
        :class="{'h-center': (this.hCentered && !this.actualBreakpoint) }"
        :style="customCarouselWrapperStyle"
      )
        SimpleCarouselSSR(
          ref="carousel"
          :options="carouselOptions"
          @breakpoint="handleBreakpoint"
        )
          .inner-element(v-for="(item, index) in items" :key="index")
            slot(
              name="default"
              :item="item"
              :index="index"
            )
</template>

<script>
  import { mapState } from 'vuex';
  import SimpleCarouselSSR from '@components/SimpleCarouselSSR';

  export default {
    name: 'ReactiveToItemsCarousel',

    components: {
      SimpleCarouselSSR,
    },

    inject: ['getObjProperty'],

    props: {
      slidesToShow: {
        type: Number,
        default() {
          return 0;
        },
      },

      itemTotalWidth: {
        type: Number,
        default() {
          return 200;
        },
      },

      breakpointOffset: {
        type: Number,
        default() {
          return 0;
        },
      },

      items: {
        type: Array,
        default() {
          return [];
        },
      },

      hCentered: {
        type: Boolean,
        default() {
          return true;
        },
      },
    },

    data() {
      return {
        actualBreakpoint: 0,
      };
    },

    computed: {
      ...mapState('global', ['windowData']),

      carouselOptions() {
        return {
          slidesToShow: this.slidesToShow,
          slidesPerRow: 1,
          dots: true,
          arrows: false,
          accessibility: true,
          touchMove: false,
          focusOnSelect: true,
          initialSlide: 0,
          infinite: true,
          swipeToSlide: true,
          responsive: [
            {
              breakpoint: Math.min(this.itemsTotalWidth + this.breakpointOffset, 1300),
              widthAdjustment: 0.34,
              settings: {
                swipe: true,
                slidesToShow: this.slidesToShow - (this.slidesToShow === this.items.length ? 1 : 0),
                autoplay: this.slidesToShow > this.items.length,
                touchMove: true,
                focusOnSelect: true,
              },
            },
          ],
        };
      },

      customCarouselWrapperStyle() {
        let style = {
          'max-width': `${this.itemsTotalWidth}px`,
        };

        return style;
      },

      itemsTotalWidth() {
        return this.slidesToShow * this.itemTotalWidth;
      },
    },

    watch: {
      itemTotalWidth: 'syncInternalStyles',
    },

    mounted() {
      this.$nextTick(() => {
        this.syncInternalStyles();
      });
      window.addEventListener('resize', this.syncInternalStyles, false);
    },

    beforeDestroy() {
      window.removeEventListener(this.syncInternalStyles);
    },

    methods: {
      syncInternalStyles() {
        let offset = 0;

        if (this.carouselOptions.responsive) {
          let responsive = this.carouselOptions.responsive.reduce(
            (prev, current) => {
              if (prev.breakpoint < current.breakpoint && current.breakpoint >= this.windowData.width) {
                return current;
              } else {
                return prev;
              }
            },
            { breakpoint: -1 }
          );

          if (responsive.widthAdjustment) {
            offset = this.itemTotalWidth * responsive.widthAdjustment * this.items.length;
          }
        }

        let slickList = this.$el.querySelector('.slick-list');

        if (slickList) {
          slickList.style.setProperty('width', `${this.itemsTotalWidth - offset}px`);
          this.$refs.carousel.goTo(0);
        }
      },

      handleBreakpoint(breakpoint) {
        this.syncInternalStyles();
        this.actualBreakpoint = breakpoint;
      },
    },
  };
</script>

<style lang="stylus">
  .reactive-to-items-carousel
    .carousel-wrapper
      overflow hidden

      .the-carousel
        width 100%

        .slick-dots
          margin 16px 3px
          margin-bottom 20px
          text-align left
          bottom 0
          top 0
          position relative

          &:hover
            cursor pointer

          li
            margin 0 -3px

          li button:before
            width 6px
            height 6px
            border 0px solid #ffffff
            border-radius 100px
            opacity 100
            content ''
            background-color #c4c4c4

          li button:focus
            outline none

        .slick-dots li.slick-active button:before
          background-color #600057
</style>
